import type { ReactNode } from 'react';
import { useAppDensity } from '../app/AppContext';
import { AdminPanel } from './AdminPanel';
import { OtsSelect } from './OtsSelect';
import {
  OtsSelectableList,
  type OtsSelectableListItem,
} from './OtsSelectableList';

export type OtsMasterDetailItem = OtsSelectableListItem & {
  /** Plain-text description used by the narrow-layout selector. */
  label: string;
};
export type OtsMasterDetailProps = {
  navigationTitle: string;
  navigationLabel: string;
  items: OtsMasterDetailItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
  /** Loading, error, or empty navigation content; details remain mounted. */
  navigationState?: ReactNode;
  /** Use a selector at every width for navigation nested inside another workspace. */
  navigationMode?: 'rail' | 'selector';
  hideNavigation?: boolean;
  /** Fill a bounded AdminPage body, with independent navigation/detail scrolling. */
  fill?: boolean;
  compact?: boolean;
  children: ReactNode;
};

/** Container-responsive navigation and details, with optional bounded-pane scrolling. */
export const OtsMasterDetail = ({
  navigationTitle,
  navigationLabel,
  items,
  selectedId,
  onSelect,
  navigationState,
  navigationMode = 'rail',
  hideNavigation = false,
  fill = false,
  compact: compactProp,
  children,
}: OtsMasterDetailProps) => {
  const density = useAppDensity();
  const compact = compactProp ?? density === 'compact';
  const rail = navigationMode === 'rail';
  const selector = (
    <OtsSelect
      label={navigationLabel}
      value={selectedId}
      options={items.map(({ id, label, disabled }) => ({
        value: id,
        label,
        disabled,
      }))}
      onChange={(id) => {
        if (id && items.some((item) => item.id === id && !item.disabled))
          onSelect(id);
      }}
      placeholder="Select an item"
      size={compact ? 'compact' : 'default'}
    />
  );
  return (
    <div
      className={
        fill
          ? '@container/master-detail flex min-h-0 min-w-0 flex-1 flex-col'
          : '@container/master-detail min-w-0'
      }
    >
      <div
        className={
          rail && !hideNavigation
            ? fill
              ? 'grid min-h-0 min-w-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-4 @3xl/master-detail:grid-cols-[14rem_minmax(0,1fr)] @3xl/master-detail:grid-rows-[minmax(0,1fr)]'
              : 'grid min-w-0 items-start gap-4 @3xl/master-detail:grid-cols-[14rem_minmax(0,1fr)]'
            : fill
            ? 'flex min-h-0 min-w-0 flex-1 flex-col gap-4'
            : 'min-w-0 space-y-4'
        }
      >
        {!hideNavigation && (
          <>
            {!navigationState && (
              <div
                className={
                  rail ? 'shrink-0 @3xl/master-detail:hidden' : 'shrink-0'
                }
              >
                {selector}
              </div>
            )}
            {!rail && navigationState}
            {rail && (
              <aside
                aria-label={navigationTitle}
                className={`${navigationState ? 'block' : 'hidden'} min-w-0 ${
                  fill
                    ? 'min-h-0 self-stretch @3xl/master-detail:block'
                    : 'self-start @3xl/master-detail:sticky @3xl/master-detail:top-4 @3xl/master-detail:block'
                }`}
              >
                <AdminPanel
                  fill={fill}
                  className={fill ? 'h-full' : undefined}
                  title={
                    <span className="flex items-center justify-between gap-2">
                      {navigationTitle}
                      <span className="text-xs font-normal tabular-nums text-[var(--ots-text-muted)]">
                        {items.length}
                      </span>
                    </span>
                  }
                  compact={compact}
                  bodyClassName="bg-[var(--ots-surface-variant)]"
                >
                  {navigationState ? (
                    <div className="p-3">{navigationState}</div>
                  ) : (
                    <div
                      className={
                        fill
                          ? 'h-full min-h-0 overflow-y-auto overscroll-contain p-3'
                          : 'max-h-[60dvh] overflow-y-auto overscroll-contain p-3'
                      }
                    >
                      <OtsSelectableList
                        ariaLabel={navigationLabel}
                        items={items}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        compact={compact}
                      />
                    </div>
                  )}
                </AdminPanel>
              </aside>
            )}
          </>
        )}
        <div
          className={
            fill
              ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-auto'
              : 'min-w-0 space-y-4'
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
};
